const PMB2BPricePillVisibility = (() => {
  const PRICE_MATCH_TOLERANCE_CENTS = 1;
  const STOREFRONT_BATCH_SIZE = 50;

  function roundMinorUnits(value) {
    const parsedValue = Number(value);
    if (!Number.isFinite(parsedValue)) return null;

    return Math.round(parsedValue);
  }

  function parseMinorUnitValue(rawValue) {
    if (rawValue === null || rawValue === undefined || rawValue === '') return null;
    return roundMinorUnits(rawValue);
  }

  function parseMajorAmountToMinorUnits(rawAmount) {
    const parsedAmount = Number(rawAmount);
    if (!Number.isFinite(parsedAmount)) return null;

    return Math.round(parsedAmount * 100);
  }

  function pricesMatchWithinTolerance(
    leftPrice,
    rightPrice,
    toleranceCents = PRICE_MATCH_TOLERANCE_CENTS
  ) {
    const normalizedLeftPrice = parseMinorUnitValue(leftPrice);
    const normalizedRightPrice = parseMinorUnitValue(rightPrice);
    const normalizedTolerance = Math.max(
      0,
      roundMinorUnits(toleranceCents) ?? PRICE_MATCH_TOLERANCE_CENTS
    );

    if (!Number.isFinite(normalizedLeftPrice) || !Number.isFinite(normalizedRightPrice)) {
      return {
        matches: false,
        differenceCents: null,
        reason: 'invalid-price',
      };
    }

    const differenceCents = Math.abs(normalizedLeftPrice - normalizedRightPrice);

    return {
      matches: differenceCents <= normalizedTolerance,
      differenceCents,
      reason: differenceCents <= normalizedTolerance ? 'within-tolerance' : 'outside-tolerance',
    };
  }

  function shouldHideB2bPricePill({
    retailPrice,
    b2bPrice,
    toleranceCents = PRICE_MATCH_TOLERANCE_CENTS,
  } = {}) {
    const retailPriceCents = parseMinorUnitValue(retailPrice);
    const b2bPriceCents = parseMinorUnitValue(b2bPrice);

    if (!Number.isFinite(retailPriceCents) || retailPriceCents <= 0) {
      return {
        matched: false,
        shouldHide: true,
        differenceCents: null,
        reason: 'missing-retail-price',
      };
    }

    if (!Number.isFinite(b2bPriceCents) || b2bPriceCents <= 0) {
      return {
        matched: false,
        shouldHide: true,
        differenceCents: null,
        reason: 'missing-b2b-price',
      };
    }

    const comparison = pricesMatchWithinTolerance(
      retailPriceCents,
      b2bPriceCents,
      toleranceCents
    );

    return {
      matched: comparison.matches,
      shouldHide: comparison.matches,
      differenceCents: comparison.differenceCents,
      reason: comparison.matches ? 'retail-matches-b2b-price' : comparison.reason,
    };
  }

  if (typeof document !== 'undefined') {
    (() => {
      const STOREFRONT_VARIANT_RETAIL_PRICE_QUERY = `#graphql
        query PMVariantRetailPrice($id: ID!, $country: CountryCode)
        @inContext(country: $country) {
          node(id: $id) {
            ... on ProductVariant {
              id
              price {
                amount
                currencyCode
              }
            }
          }
        }
      `;
      const STOREFRONT_VARIANT_RETAIL_PRICES_QUERY = `#graphql
        query PMVariantRetailPrices($ids: [ID!]!, $country: CountryCode)
        @inContext(country: $country) {
          nodes(ids: $ids) {
            ... on ProductVariant {
              id
              price {
                amount
                currencyCode
              }
            }
          }
        }
      `;

      const retailPriceRequestCache = new Map();
      let enhancementFrame = null;

      function normalizeCountryCode(countryCode) {
        return typeof countryCode === 'string' && countryCode.trim() !== ''
          ? countryCode.trim().toUpperCase()
          : null;
      }

      function buildCacheKey(variantGid, countryCode) {
        return `${variantGid}::${countryCode || ''}`;
      }

      function chunkArray(values, chunkSize) {
        const chunks = [];
        for (let index = 0; index < values.length; index += chunkSize) {
          chunks.push(values.slice(index, index + chunkSize));
        }

        return chunks;
      }

      function getStorefrontAccessToken() {
        const configToken = window.pmStorefrontApiConfig?.accessToken;
        if (typeof configToken === 'string' && configToken.trim() !== '') {
          return configToken.trim();
        }

        const legacyToken = window.pmStorefrontEstimatorConfig?.accessToken;
        if (typeof legacyToken === 'string' && legacyToken.trim() !== '') {
          return legacyToken.trim();
        }

        return '';
      }

      function getStorefrontApiVersion() {
        const configVersion = window.pmStorefrontApiConfig?.apiVersion;
        if (typeof configVersion === 'string' && /^\d{4}-\d{2}$/.test(configVersion.trim())) {
          return configVersion.trim();
        }

        const legacyVersion = window.pmStorefrontEstimatorConfig?.apiVersion;
        if (typeof legacyVersion === 'string' && /^\d{4}-\d{2}$/.test(legacyVersion.trim())) {
          return legacyVersion.trim();
        }

        return '2025-01';
      }

      function getStorefrontGraphQLEndpoint() {
        return `/api/${getStorefrontApiVersion()}/graphql.json`;
      }

      async function requestStorefrontGraphQL(query, variables = {}) {
        const accessToken = getStorefrontAccessToken();
        if (!accessToken) {
          throw new Error('Missing Storefront API access token.');
        }

        const response = await fetch(getStorefrontGraphQLEndpoint(), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': accessToken,
          },
          body: JSON.stringify({
            query,
            variables,
          }),
        });

        if (!response.ok) {
          throw new Error('B2B price pill Storefront request failed.');
        }

        const payload = await response.json();
        if (Array.isArray(payload?.errors) && payload.errors.length > 0) {
          throw new Error(
            payload.errors[0]?.message || 'B2B price pill Storefront query failed.'
          );
        }

        return payload?.data || {};
      }

      function fetchVariantRetailPrice(
        variantGid,
        countryCode,
        { forceRefresh = false } = {}
      ) {
        const normalizedVariantGid =
          typeof variantGid === 'string' ? variantGid.trim() : '';
        const normalizedCountryCode = normalizeCountryCode(countryCode);

        if (!normalizedVariantGid) {
          return Promise.resolve(null);
        }

        const cacheKey = buildCacheKey(normalizedVariantGid, normalizedCountryCode);
        if (!forceRefresh && retailPriceRequestCache.has(cacheKey)) {
          return retailPriceRequestCache.get(cacheKey);
        }

        const request = requestStorefrontGraphQL(STOREFRONT_VARIANT_RETAIL_PRICE_QUERY, {
          id: normalizedVariantGid,
          country: normalizedCountryCode,
        })
          .then((data) => data?.node?.price || null)
          .catch((error) => {
            retailPriceRequestCache.delete(cacheKey);
            throw error;
          });

        retailPriceRequestCache.set(cacheKey, request);
        return request;
      }

      function cacheRetailPriceBatch(variantGids, countryCode) {
        const normalizedCountryCode = normalizeCountryCode(countryCode);
        const normalizedVariantGids = Array.from(
          new Set(
            variantGids
              .map((variantGid) => (typeof variantGid === 'string' ? variantGid.trim() : ''))
              .filter(Boolean)
          )
        );

        if (!normalizedVariantGids.length) {
          return [];
        }

        return chunkArray(normalizedVariantGids, STOREFRONT_BATCH_SIZE).map((variantBatch) => {
          const batchPromise = requestStorefrontGraphQL(
            STOREFRONT_VARIANT_RETAIL_PRICES_QUERY,
            {
              ids: variantBatch,
              country: normalizedCountryCode,
            }
          )
            .then((data) => {
              const pricesByVariant = new Map();

              (data?.nodes || []).forEach((node) => {
                if (node?.id) {
                  pricesByVariant.set(node.id, node.price || null);
                }
              });

              return pricesByVariant;
            })
            .catch((error) => {
              variantBatch.forEach((variantGid) => {
                retailPriceRequestCache.delete(buildCacheKey(variantGid, normalizedCountryCode));
              });

              throw error;
            });

          variantBatch.forEach((variantGid) => {
            retailPriceRequestCache.set(
              buildCacheKey(variantGid, normalizedCountryCode),
              batchPromise.then((pricesByVariant) => pricesByVariant.get(variantGid) || null)
            );
          });

          return batchPromise;
        });
      }

      function collectEnhancementTargets(root = document) {
        const scope = root instanceof HTMLElement || root instanceof Document ? root : document;
        const targets = [];

        scope.querySelectorAll('[data-b2b-price-pill-visibility]').forEach((node) => {
          if (!(node instanceof HTMLElement)) return;

          const variantGid = node.dataset.variantGid?.trim() || '';
          const b2bPriceCents = parseMinorUnitValue(node.dataset.currentB2bPriceCents);
          const countryCode = normalizeCountryCode(node.dataset.marketCountryCode);

          if (!variantGid || !Number.isFinite(b2bPriceCents) || b2bPriceCents <= 0 || !countryCode) {
            return;
          }

          targets.push({
            node,
            variantGid,
            b2bPriceCents,
            countryCode,
          });
        });

        return targets;
      }

      function applyVisibility(node, shouldHide) {
        if (!(node instanceof HTMLElement)) return;

        delete node.dataset.b2bPricePillPending;
        node.removeAttribute('aria-hidden');
        node.hidden = shouldHide;
        node.style.display = shouldHide ? 'none' : '';

        const pillGroup = node.closest('.price__pill-group');
        if (!(pillGroup instanceof HTMLElement)) return;

        const hasVisiblePill = Array.from(
          pillGroup.querySelectorAll('[data-b2b-price-pill-visibility]')
        ).some((pillNode) => {
          if (!(pillNode instanceof HTMLElement)) return false;
          return pillNode.hidden === false && pillNode.style.display !== 'none';
        });

        pillGroup.hidden = !hasVisiblePill;
        pillGroup.style.display = hasVisiblePill ? '' : 'none';
      }

      async function enhancePillsWithin(root = document) {
        const targets = collectEnhancementTargets(root);
        if (!targets.length) return;
        const accessToken = getStorefrontAccessToken();

        if (!accessToken) {
          targets.forEach(({ node }) => {
            applyVisibility(node, true);
            node.dataset.b2bPricePillVisibilityState = 'hidden-unverified';
          });
          return;
        }

        const missingVariantIdsByCountry = new Map();

        targets.forEach(({ variantGid, countryCode }) => {
          const cacheKey = buildCacheKey(variantGid, countryCode);
          if (retailPriceRequestCache.has(cacheKey)) return;

          if (!missingVariantIdsByCountry.has(countryCode)) {
            missingVariantIdsByCountry.set(countryCode, []);
          }

          missingVariantIdsByCountry.get(countryCode).push(variantGid);
        });

        const batchRequests = [];
        missingVariantIdsByCountry.forEach((variantGids, countryCode) => {
          batchRequests.push(...cacheRetailPriceBatch(variantGids, countryCode));
        });

        if (batchRequests.length) {
          await Promise.allSettled(batchRequests);
        }

        await Promise.all(
          targets.map(async ({ node, variantGid, b2bPriceCents, countryCode }) => {
            try {
              const cacheKey = buildCacheKey(variantGid, countryCode);
              let retailPrice = retailPriceRequestCache.has(cacheKey)
                ? await retailPriceRequestCache.get(cacheKey)
                : null;

              // Keep the batch path for larger surfaces, but if a specific variant still comes
              // back empty, retry through the same single-variant Storefront query that powers
              // the PDP RRP helper. That keeps the pill hide/show decision aligned with the
              // proven retail/base reference path instead of silently leaving the pill visible.
              if (!retailPrice?.amount) {
                retailPrice = await fetchVariantRetailPrice(variantGid, countryCode, {
                  forceRefresh: true,
                });
              }

              const retailPriceCents = parseMajorAmountToMinorUnits(retailPrice?.amount);
              const visibility = shouldHideB2bPricePill({
                retailPrice: retailPriceCents,
                b2bPrice: b2bPriceCents,
              });

              // Only reveal the pill when a trustworthy comparison price exists and a real
              // discount remains. Missing retail/base data means there is no comparison price
              // to show, so the pill stays hidden.
              applyVisibility(node, visibility.shouldHide);
              if (visibility.shouldHide) {
                node.dataset.b2bPricePillVisibilityState =
                  visibility.reason === 'missing-retail-price'
                    ? 'hidden-missing-reference'
                    : 'hidden-no-discount';
              } else {
                node.dataset.b2bPricePillVisibilityState = 'visible-discounted';
              }
            } catch (error) {
              applyVisibility(node, true);
              node.dataset.b2bPricePillVisibilityState = 'hidden-error';
              console.error(
                '[B2BPricePillVisibility] Failed to resolve retail reference',
                error
              );
            }
          })
        );
      }

      function queueEnhancement(root = document) {
        if (enhancementFrame !== null) {
          cancelAnimationFrame(enhancementFrame);
        }

        enhancementFrame = requestAnimationFrame(() => {
          enhancementFrame = null;
          enhancePillsWithin(root).catch((error) => {
            console.error('[B2BPricePillVisibility] Failed to enhance price pills', error);
          });
        });
      }

      function initializeMutationObserver() {
        if (typeof MutationObserver === 'undefined') return;

        const observer = new MutationObserver((mutations) => {
          let shouldRefresh = false;

          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
              if (!(node instanceof HTMLElement)) return;

              if (
                node.matches?.('[data-b2b-price-pill-visibility]') ||
                node.querySelector?.('[data-b2b-price-pill-visibility]')
              ) {
                shouldRefresh = true;
              }
            });
          });

          if (shouldRefresh) {
            queueEnhancement(document);
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });
      }

      function initialize() {
        queueEnhancement(document);

        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS?.variantChange
        ) {
          subscribe(PUB_SUB_EVENTS.variantChange, () => {
            queueEnhancement(document);
          });
        }

        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS?.cartUpdate
        ) {
          subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
            queueEnhancement(document);
          });
        }

        document.addEventListener('product-info:loaded', () => {
          queueEnhancement(document);
        });

        initializeMutationObserver();
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initialize, { once: true });
      } else {
        initialize();
      }
    })();
  }

  return {
    PRICE_MATCH_TOLERANCE_CENTS,
    parseMajorAmountToMinorUnits,
    parseMinorUnitValue,
    pricesMatchWithinTolerance,
    roundMinorUnits,
    shouldHideB2bPricePill,
  };
})();

if (typeof module !== 'undefined' && module.exports) {
  module.exports = PMB2BPricePillVisibility;
}
